// SPDX-License-Identifier: AGPL-3.0-or-later

import styles from '@app/features/app/components/layout/ChannelItem.module.css';
import MediaEngine from '@app/features/voice/engine/MediaEngineFacade';
import VoiceChannelOccupancyStore from '@app/features/voice/state/VoiceChannelOccupancyStore';
import {useVoiceChannelOccupancyDurationLabel} from '@app/features/voice/utils/VoiceChannelOccupancyUtils';
import {observer} from 'mobx-react-lite';
import type React from 'react';
import {useLayoutEffect, useRef} from 'react';

interface VoiceChannelOccupancyTimerProps {
	guildId: string;
	channelId: string;
	userCount: number;
}

export const VoiceChannelOccupancyTimer: React.FC<VoiceChannelOccupancyTimerProps> = observer(
	({guildId, channelId, userCount}) => {
		const previousCountRef = useRef<number | null>(null);
		useLayoutEffect(() => {
			const occupants = MediaEngine.getAllVoiceStatesInChannel(guildId, channelId);
			VoiceChannelOccupancyStore.syncFromVoiceStates(
				guildId,
				Object.values(occupants).map((occupant) => ({
					channel_id: channelId,
					channel_occupied_since: occupant.channel_occupied_since,
					occupied_since: occupant.occupied_since,
					started_at: occupant.started_at,
				})),
			);
			if (userCount <= 0) {
				VoiceChannelOccupancyStore.syncLocalCount(guildId, channelId, userCount);
				previousCountRef.current = 0;
				return;
			}
			if (previousCountRef.current === 0) {
				VoiceChannelOccupancyStore.seedIfChannelBecameOccupied(guildId, channelId);
			}
			previousCountRef.current = userCount;
		}, [guildId, channelId, userCount]);
		void VoiceChannelOccupancyStore.getOccupiedSinceUnix(guildId, channelId);
		const label = useVoiceChannelOccupancyDurationLabel(guildId, channelId, userCount);
		if (!label) {
			return null;
		}
		return (
			<span className={styles.voiceOccupancyTimer} aria-hidden data-flx="app.voice-channel-occupancy-timer">
				{label}
			</span>
		);
	},
);
